import {
  maintenanceSessionDispatchSchema, maintenanceTaskSchema, maintenanceStartGrantSchema,
  maintenanceCapabilitiesSchema, nativeMaintenanceSnapshotSchema, maintenanceEventSchema,
  parseMaintenancePayload,
  type IMaintenanceNativeService, type MaintenanceId, type MaintenanceEvent,
  type MaintenanceSessionDispatch, type SignedMaintenanceEnvelope,
} from '@playcastdotio/Messaging';
import { MaintenanceNativeRequestService } from '../../../services/maintenance/MaintenanceActivityService';

type NativeSessionService = Pick<IMaintenanceNativeService, 'capabilities' | 'snapshot' | 'start' | 'reconcile'>;
interface SessionDependencies {
  native: NativeSessionService;
  ownership: { claimMaintenanceExecution(executionId: string): void };
  machineIdentity: { getMachineId(): string };
  enabled: boolean;
  onDisconnected?(listener: () => void): () => void;
  now?: () => number;
  taskHash?(envelope: SignedMaintenanceEnvelope): Promise<string>;
}
const identityKey = (id: MaintenanceId) => JSON.stringify([
  id.machineId, id.jobId, id.executionId, id.stepId, id.attemptId, id.revision,
]);
const terminal = new Set(['succeeded', 'failed', 'cancelled', 'expired']);

/** The native service verifies signatures/admission and retains all events; Host owns no attempt journal. */
export class HostMaintenanceSessionNativeService implements NativeSessionService {
  constructor(private readonly requests: MaintenanceNativeRequestService) {}
  capabilities() { return this.requests.call('maintenanceCapabilities', {}, value => maintenanceCapabilitiesSchema.parse(value)); }
  snapshot() { return this.requests.call('maintenanceSnapshot', {}, value => nativeMaintenanceSnapshotSchema.parse(value)); }
  start(task: SignedMaintenanceEnvelope, grant: SignedMaintenanceEnvelope) {
    return this.requests.call('maintenanceStart', { task, grant }, value => maintenanceEventSchema.parse(value) as MaintenanceEvent);
  }
  reconcile(identity: MaintenanceId) {
    return this.requests.call('maintenanceReconcile', { identity }, value => maintenanceEventSchema.parse(value) as MaintenanceEvent);
  }
}

/** Routes signed session grants to the common native ledger; never executes legacy script content. */
export class MaintenanceSessionManager {
  private readonly pending = new Map<string, { fingerprint: string; promise: Promise<void> }>();
  private readonly active = new Map<string, { identity: MaintenanceId; sessionSid: string }>();
  private readonly sessionEpochs = new Map<string, number>();
  private readonly now: () => number;
  private unsubscribe?: () => void;
  private disposed = false;
  private connectionEpoch = 0;

  constructor(private readonly deps: SessionDependencies) {
    this.now = deps.now ?? Date.now;
  }

  init(): void {
    if (this.unsubscribe || this.disposed) return;
    this.unsubscribe = this.deps.onDisconnected?.(() => {
      this.connectionEpoch++;
      for (const sid of new Set([...this.active.values()].map(item => item.sessionSid))) {
        void this.handleSessionClosed(sid).catch(() => undefined);
      }
    });
  }

  async handleDispatch(message: MaintenanceSessionDispatch): Promise<void> {
    if (this.disposed || !this.deps.enabled) throw new Error('maintenance_session_disabled');
    const parsed = maintenanceSessionDispatchSchema.parse(message);
    const key = identityKey(parsed.identity);
    const fingerprint = JSON.stringify([parsed.task, parsed.grant]);
    const pending = this.pending.get(key);
    if (pending) {
      if (pending.fingerprint !== fingerprint) throw new Error('maintenance_session_grant_conflict');
      return pending.promise;
    }
    if (this.pending.size >= 128) throw new Error('maintenance_session_busy');
    const promise = this.dispatch(parsed).finally(() => { this.pending.delete(key); });
    this.pending.set(key, { fingerprint, promise });
    return promise;
  }

  async handleSessionClosed(sessionSid: string): Promise<void> {
    this.sessionEpochs.set(sessionSid, (this.sessionEpochs.get(sessionSid) ?? 0) + 1);
    const results = await Promise.allSettled([...this.active.entries()].filter(([, item]) => item.sessionSid === sessionSid)
      .map(async ([key, item]) => {
        const event = await this.deps.native.reconcile(item.identity);
        this.validateIdentity(event, item.identity);
        if (terminal.has(event.execution)) this.active.delete(key);
      }));
    if (results.some(result => result.status === 'rejected')) throw new Error('maintenance_reconciliation_required');
  }

  dispose(): void {
    this.disposed = true;
    this.connectionEpoch++;
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    for (const sid of new Set([...this.active.values()].map(item => item.sessionSid))) {
      void this.handleSessionClosed(sid).catch(() => undefined);
    }
  }

  private async dispatch(message: MaintenanceSessionDispatch): Promise<void> {
    // Decoding is for routing/correlation only. Native verifies the exact signed bytes before any effect.
    const task = parseMaintenancePayload(message.task.payloadBase64, maintenanceTaskSchema);
    const grant = parseMaintenancePayload(message.grant.payloadBase64, maintenanceStartGrantSchema);
    const key = identityKey(message.identity);
    if (identityKey(task) !== key || identityKey(grant) !== key || task.machineId !== this.deps.machineIdentity.getMachineId()
      || grant.executor !== 'host-session' || task.context.kind === 'machine-system' || !grant.sessionSid
      || (task.context.sessionSid && task.context.sessionSid !== grant.sessionSid)
      || Date.parse(grant.expiresAtUtc) <= this.now() || Date.parse(grant.issuedAtUtc) > this.now() + 5_000) {
      throw new Error('maintenance_session_route_mismatch');
    }
    const hash = this.deps.taskHash ? await this.deps.taskHash(message.task) : await this.hash(message.task);
    if (hash !== grant.taskSha256) throw new Error('maintenance_session_task_mismatch');
    const epoch = this.sessionEpochs.get(grant.sessionSid) ?? 0;
    const connectionEpoch = this.connectionEpoch;
    const capabilities = await this.deps.native.capabilities();
    if (capabilities.nativeProtocolVersion !== 1 || !capabilities.durableAttempts || !capabilities.signedEnvelope
      || !capabilities.policies.includes(task.policy.hostMode) || !capabilities.executions.some(tuple =>
        tuple.operation === task.operation && tuple.context === task.context.kind && tuple.executor === 'host-session')) {
      throw new Error('maintenance_unsupported_capability');
    }
    const snapshot = await this.deps.native.snapshot();
    if (snapshot.generation !== grant.nativeGeneration || snapshot.sessionSid !== grant.sessionSid
      || Math.abs(Date.parse(snapshot.observedAtUtc) - this.now()) > 30_000) throw new Error('maintenance_session_unavailable');
    if (connectionEpoch !== this.connectionEpoch) throw new Error('maintenance_session_closed');
    this.active.set(key, { identity: message.identity, sessionSid: grant.sessionSid });
    this.deps.ownership.claimMaintenanceExecution(task.executionId);
    const prior = await this.deps.native.reconcile(message.identity);
    this.validateIdentity(prior, message.identity);
    if (prior.execution !== 'waiting' || prior.preparation !== 'ready') {
      if (terminal.has(prior.execution)) this.active.delete(key);
      return;
    }
    if (this.disposed || connectionEpoch !== this.connectionEpoch || epoch !== (this.sessionEpochs.get(grant.sessionSid) ?? 0)
      || Date.parse(grant.expiresAtUtc) <= this.now()) throw new Error('maintenance_session_closed');
    const started = await this.deps.native.start(message.task, message.grant);
    this.validateIdentity(started, message.identity);
    if (terminal.has(started.execution)) this.active.delete(key);
  }

  private validateIdentity(event: MaintenanceEvent, identity: MaintenanceId): void {
    if (identityKey(event) !== identityKey(identity)) throw new Error('maintenance_native_identity_mismatch');
  }

  private async hash(envelope: SignedMaintenanceEnvelope): Promise<string> {
    const bytes = Uint8Array.from(atob(envelope.payloadBase64), character => character.charCodeAt(0));
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  }
}
